'use client';

import React, { useRef, useState } from 'react';

import { styles } from './UiToolScrollDraggable.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface UiToolScrollDraggableProps extends NativeElement {
  children?: React.ReactNode;
  vertical?: boolean;
  dragThreshold?: number;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export function UiToolScrollDraggable({
  children,
  vertical = false,
  dragThreshold = 5,
  className,
  onClick,
  ...props
}: UiToolScrollDraggableProps) {
  const slots = styles();
  const scrollRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const scrollLeft = useRef(0);
  const scrollTop = useRef(0);

  const [dragged, setDragged] = useState(false);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;
    isDragging.current = true;
    setDragged(false);

    if (vertical) {
      startY.current = e.pageY - scrollRef.current.offsetTop;
      scrollTop.current = scrollRef.current.scrollTop;
    } else {
      startX.current = e.pageX - scrollRef.current.offsetLeft;
      scrollLeft.current = scrollRef.current.scrollLeft;
    }
    scrollRef.current.style.cursor = 'grabbing';

    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;

    let walk: number;

    if (vertical) {
      e.preventDefault();
      const y = e.pageY - scrollRef.current.offsetTop;
      walk = y - startY.current;

      if (!dragged && Math.abs(walk) > dragThreshold) {
        setDragged(true);
      }

      scrollRef.current.scrollTop = scrollTop.current - walk;
    } else {
      e.preventDefault();
      const x = e.pageX - scrollRef.current.offsetLeft;
      walk = x - startX.current;

      if (!dragged && Math.abs(walk) > dragThreshold) {
        setDragged(true);
      }

      scrollRef.current.scrollLeft = scrollLeft.current - walk;
    }
  };

  const onMouseUp = () => {
    if (!scrollRef.current) return;

    isDragging.current = false;
    setTimeout(() => setDragged(false), 0);

    scrollRef.current.style.cursor = 'grab';
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('mousemove', onMouseMove);
  };

  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragged) {
      e.preventDefault();
      e.stopPropagation();
    } else if (onClick) {
      onClick(e);
    }
  };

  return (
    <div
      ref={scrollRef}
      className={slots.base({ className })}
      style={{ cursor: 'grab' }}
      onMouseDown={onMouseDown}
      onClickCapture={onClickCapture}
      {...props}
    >
      {children}
    </div>
  );
}
